/**
 * Copyright (c), Vector Engineering LLC
 * All rights reserved.
 * 
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 * Commercial use requires explicit written permission.
 */

import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
// Import Three.js modules with types
import * as THREE from 'three';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

// Loader progress interface
interface LoaderProgressEvent {
    loaded: number;
    total: number;
}

interface Character3DViewerProps {
    modelPath: string;
    width?: number | string;
    height?: number | string;
    backgroundColor?: string;
    autoRotate?: boolean;
}

const Character3DViewer: React.FC<Character3DViewerProps> = ({
    modelPath,
    width = '100%',
    height = 400,
    backgroundColor = '#f5f5f5',
    autoRotate = true
}) => {
    const mountRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Scene objects references
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const controlsRef = useRef<OrbitControls | null>(null);
    const modelRef = useRef<THREE.Group | null>(null);
    const frameIdRef = useRef<number>(0);

    // Cleanup function
    const cleanupScene = () => {
        if (frameIdRef.current) {
            cancelAnimationFrame(frameIdRef.current);
        }

        if (modelRef.current) {
            sceneRef.current?.remove(modelRef.current);
            modelRef.current = null;
        }

        if (rendererRef.current && rendererRef.current.domElement) {
            rendererRef.current.dispose();
            rendererRef.current = null;
        }

        if (controlsRef.current) {
            controlsRef.current.dispose();
            controlsRef.current = null;
        }

        // Clear the container
        if (mountRef.current) {
            while (mountRef.current.firstChild) {
                mountRef.current.removeChild(mountRef.current.firstChild);
            }
        }

        sceneRef.current = null;
        cameraRef.current = null;
    };

    // Setup and render loop
    useEffect(() => {
        if (!mountRef.current) return;

        // Initialize scene, camera, and renderer
        const scene = new THREE.Scene();
        sceneRef.current = scene;
        scene.background = new THREE.Color(backgroundColor);

        // Add lights
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
        scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
        directionalLight.position.set(1, 1, 2);
        scene.add(directionalLight);

        const backLight = new THREE.DirectionalLight(0xffffff, 0.5);
        backLight.position.set(-1, 1, -1);
        scene.add(backLight);

        // Camera setup
        const container = mountRef.current;
        const aspect = container.clientWidth / container.clientHeight;
        const camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 1000);
        camera.position.set(0, 1, 3);
        cameraRef.current = camera;

        // Renderer setup
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(container.clientWidth, container.clientHeight);
        renderer.setClearColor(backgroundColor);
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        container.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // Controls setup
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.target.set(0, 1, 0);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.autoRotate = autoRotate;
        controls.autoRotateSpeed = 1;
        controls.enablePan = false;
        controls.minDistance = 1.5;
        controls.maxDistance = 5;
        controls.maxPolarAngle = Math.PI / 1.5;
        controls.update();
        controlsRef.current = controls;

        // Animation loop
        const animate = () => {
            frameIdRef.current = requestAnimationFrame(animate);

            if (controlsRef.current) {
                controlsRef.current.update();
            }

            if (rendererRef.current && sceneRef.current && cameraRef.current) {
                rendererRef.current.render(sceneRef.current, cameraRef.current);
            }
        };

        // Load the GLB model
        const loader = new GLTFLoader();

        setIsLoading(true);
        loader.load(
            modelPath,
            (gltf: GLTF) => {
                const model = gltf.scene;

                // Scale and position the model
                model.scale.set(1, 1, 1);
                model.position.set(0, 0, 0);

                // Auto-center the model
                const box = new THREE.Box3().setFromObject(model);
                const center = box.getCenter(new THREE.Vector3());
                const size = box.getSize(new THREE.Vector3());

                // Adjust model position to center it
                model.position.x = -center.x;
                model.position.y = -center.y + size.y / 2;
                model.position.z = -center.z;

                // Add model to scene
                scene.add(model);
                modelRef.current = model;

                // Adjust camera distance based on model size
                const maxDim = Math.max(size.x, size.y, size.z);
                const fov = camera.fov * (Math.PI / 180);
                let cameraDistance = maxDim / (2 * Math.tan(fov / 2));
                cameraDistance *= 1.5; // Add some margin

                camera.position.set(0, size.y / 2, cameraDistance);
                camera.lookAt(new THREE.Vector3(0, size.y / 2, 0));

                if (controlsRef.current) {
                    controlsRef.current.target.set(0, size.y / 2, 0);
                    controlsRef.current.update();
                }

                // Start animation loop
                animate();
                setIsLoading(false);
            },
            (xhr: LoaderProgressEvent) => {
                const progress = xhr.loaded / xhr.total;
                console.log(`GLB model loading: ${Math.round(progress * 100)}%`);
            },
            (error: any) => {
                console.error('Error loading GLB model:', error);
                setError('Failed to load 3D model');
                setIsLoading(false);
            }
        );

        // Handle window resize
        const handleResize = () => {
            if (!container || !cameraRef.current || !rendererRef.current) return;

            const width = container.clientWidth;
            const height = container.clientHeight;

            cameraRef.current.aspect = width / height;
            cameraRef.current.updateProjectionMatrix();

            rendererRef.current.setSize(width, height);
        };

        window.addEventListener('resize', handleResize);

        // Clean up on unmount
        return () => {
            window.removeEventListener('resize', handleResize);
            cleanupScene();
        };
    }, [modelPath, backgroundColor, autoRotate]);

    return (
        <Box
            ref={mountRef}
            sx={{
                width,
                height,
                position: 'relative',
                borderRadius: 1,
                overflow: 'hidden',
                backgroundColor
            }}
        >
            {isLoading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(245, 245, 245, 0.7)',
                        zIndex: 10
                    }}
                >
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                        Loading 3D model...
                    </Typography>
                </Box>
            )}

            {error && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(245, 245, 245, 0.7)',
                        zIndex: 10,
                        color: 'error.main'
                    }}
                >
                    <Typography variant="body1">{error}</Typography>
                </Box>
            )}
        </Box>
    );
};

export default Character3DViewer;
